import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type CheckoutStep = "billing" | "shipping" | "payment";
export type CheckoutView = "form" | "paymentIframe"; // New type to manage the view

export interface CheckoutState {
  selectedPaymentMethod: string | null;
  currentStep: CheckoutStep;
  completedSteps: CheckoutStep[];
  isCurrentStepFormValid: boolean;
  useBillingForShipping: boolean;
  checkoutView: CheckoutView; // New state property
}

const initialState: CheckoutState = {
  selectedPaymentMethod: null,
  currentStep: "billing",
  completedSteps: [],
  isCurrentStepFormValid: false,
  useBillingForShipping: true,
  checkoutView: "form", // Initial view is the form
};

export const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    setSelectedPaymentMethod: (state, action: PayloadAction<string | null>) => {
      const newMethod = action.payload;
      if (state.selectedPaymentMethod !== newMethod) {
        state.selectedPaymentMethod = newMethod;
        if (state.currentStep === "payment") {
          state.isCurrentStepFormValid = !!newMethod;
        }
      }
    },
    setCurrentStep: (state, action: PayloadAction<CheckoutStep>) => {
      if (state.currentStep !== action.payload) {
        if (action.payload === "payment" && !!state.selectedPaymentMethod) {
          state.isCurrentStepFormValid = true;
        } else {
          state.isCurrentStepFormValid = false;
        }
      }
      state.currentStep = action.payload;
    },
    completeStep: (state, action: PayloadAction<CheckoutStep>) => {
      if (!state.completedSteps.includes(action.payload)) {
        state.completedSteps.push(action.payload);
      }
    },
    goToNextStep: (state) => {
      const steps: CheckoutStep[] = ["billing", "shipping", "payment"];
      const currentIndex = steps.indexOf(state.currentStep);

      if (!state.completedSteps.includes(state.currentStep)) {
        state.completedSteps.push(state.currentStep);
      }
      if (state.currentStep === "shipping" && state.useBillingForShipping) {
        if (!state.completedSteps.includes("shipping")) {
          state.completedSteps.push("shipping");
        }
      }

      if (currentIndex < steps.length - 1) {
        const nextStep = steps[currentIndex + 1];
        state.currentStep = nextStep;
        if (nextStep === "payment") {
          state.isCurrentStepFormValid = !!state.selectedPaymentMethod;
        } else {
          state.isCurrentStepFormValid = false;
        }
      }
    },
    goToPreviousStep: (state) => {
      const steps: CheckoutStep[] = ["billing", "shipping", "payment"];
      const currentIndex = steps.indexOf(state.currentStep);

      if (currentIndex > 0) {
        const previousStep = steps[currentIndex - 1];
        state.currentStep = previousStep;
        state.completedSteps = state.completedSteps.filter(
          (step) => steps.indexOf(step) < currentIndex
        );
        state.isCurrentStepFormValid = false;
      }
    },
    setIsCurrentStepFormValid: (state, action: PayloadAction<boolean>) => {
      if (state.isCurrentStepFormValid !== action.payload) {
        state.isCurrentStepFormValid = action.payload;
      }
    },
    setUseBillingForShipping: (state, action: PayloadAction<boolean>) => {
      if (state.useBillingForShipping !== action.payload) {
        state.useBillingForShipping = action.payload;
        if (state.currentStep === "shipping" && action.payload) {
          state.isCurrentStepFormValid = true;
        } else if (state.currentStep === "shipping" && !action.payload) {
          state.isCurrentStepFormValid = false;
        }
      }
    },
    // New reducer to control the view
    setCheckoutView: (state, action: PayloadAction<CheckoutView>) => {
      state.checkoutView = action.payload;
    },
    resetCheckout: (state) => {
      // This correctly resets the entire slice to its initial state,
      // including the new checkoutView property.
      Object.assign(state, initialState);
      console.log("[Slice Reset] Checkout state reset.");
    },
  },
});

export const {
  setSelectedPaymentMethod,
  setCurrentStep,
  completeStep,
  goToNextStep,
  goToPreviousStep,
  setIsCurrentStepFormValid,
  setUseBillingForShipping,
  setCheckoutView, // Export the new action
  resetCheckout,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
